import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Card, Form, Button, Alert, Badge, Row, Col, ListGroup } from 'react-bootstrap';
import api from '../api/axios';
import { calculatePrices } from '../utils/priceCalculator';
import JobServiceFields from './job/JobServiceFields';
import { confirmUnpriced } from './job/JobTiles';
import UnlinkButton from './job/CustomerLink';
import AdjustmentInput from './job/AdjustmentInput';
import useAdjustment from '../hooks/useAdjustment';

const AddServiceRecord = ({ vehicle, sizeId, onSave, prefillData }) => {
    const [menus, setMenus] = useState([]);
    const [options, setOptions] = useState([]);
    const [prices, setPrices] = useState({ servicePrices: [], optionPrices: [] });

    const [selectedMenu, setSelectedMenu] = useState(null);
    // 옵션은 id 배열로 보유 (서버·DB 와 동일한 모양). 가격 계산 시에만 객체로 되찾는다.
    const [optionIds, setOptionIds] = useState([]);
    const [calculatedPrice, setCalculatedPrice] = useState(0);
    // 조정금 상태는 useAdjustment 공통 훅 (모바일 대응 토글 + priceBreakdown 되읽기)
    const adjustment = useAdjustment();
    const signedAdjustment = adjustment.signed;
    const [totalPrice, setTotalPrice] = useState(0);
    const [comment, setComment] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('카드');

    // 🚀 현장 방문객을 위한 신규 고객 정보 상태
    const [newCustomerName, setNewCustomerName] = useState('');
    const [newPhoneNumber, setNewPhoneNumber] = useState('');
    // 워크보드식 고객 자동완성
    const [custSug, setCustSug] = useState({ field: null, items: [] });
    // 자동완성에서 지목한 고객 — { id, hasPhone }. 수정해도 풀리지 않고 ✕ 로만 해제한다.
    const [linkedCustomer, setLinkedCustomer] = useState(null);
    const custSearchTimer = useRef(null);

    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const fetchData = useCallback(async () => {
        try {
            const [menusRes, optionsRes, pricesRes] = await Promise.all([
                api.get('/servicemenus'),
                api.get('/options'),
                api.get('/prices'),
            ]);
            const activeMenus = menusRes.data.data.filter(m => m.isActive);
            const activeOptions = optionsRes.data.data.filter(o => o.isActive);
            
            setMenus(activeMenus);
            setOptions(activeOptions);
            setPrices(pricesRes.data.data);

            if (prefillData) {
                if (prefillData.service_menu_id) {
                    const menu = activeMenus.find(m => m.id === parseInt(prefillData.service_menu_id));
                    if (menu) setSelectedMenu(menu);
                }
                if (prefillData.optionIds && Array.isArray(prefillData.optionIds)) {
                    setOptionIds(activeOptions.filter(o => prefillData.optionIds.includes(o.id)).map(o => o.id));
                }
                if (prefillData.memo) {
                    setComment(prefillData.memo);
                }
            }
        } catch (err) {
            console.error("Error fetching service data:", err);
            setError('데이터 로딩 실패');
        }
    }, [prefillData]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const selectedOptionObjects = useMemo(
        () => options.filter(o => optionIds.includes(o.id)),
        [options, optionIds]
    );

    useEffect(() => {
        const { calculatedPrice: calc, totalPrice: total } = calculatePrices({
            selectedMenu,
            selectedOptions: selectedOptionObjects,
            prices,
            sizeId,
            adjustmentAmount: signedAdjustment
        });
        setCalculatedPrice(calc);
        setTotalPrice(total);
    }, [selectedMenu, selectedOptionObjects, prices, sizeId, signedAdjustment]);

    // JobServiceFields 는 { service_menu_id, optionIds } 로 말한다. 메뉴는 객체로 보관 중이므로 변환.
    const handleServiceChange = (patch) => {
        if ('service_menu_id' in patch) {
            setSelectedMenu(menus.find(m => m.id === parseInt(patch.service_menu_id)) || null);
        }
        if ('optionIds' in patch) {
            setOptionIds(patch.optionIds);
        }
    };

    // 워크보드식 고객 자동완성 (이름/연락처) — 미등록 차량일 때만 사용
    const handleCustSearch = (field, value) => {
        if (field === 'name') setNewCustomerName(value);
        else setNewPhoneNumber(value);

        if (custSearchTimer.current) clearTimeout(custSearchTimer.current);
        custSearchTimer.current = setTimeout(() => {
            if (field === 'name' && value.trim().length >= 2) {
                api.get(`/customers/search?name=${encodeURIComponent(value.trim())}`)
                    .then(res => setCustSug({ field: 'name', items: res.data.data || [] }))
                    .catch(() => {});
            } else if (field === 'phone') {
                const digits = value.replace(/\D/g, '');
                if (digits.length === 4 || digits.length >= 10) {
                    api.get(`/customers/search?phone=${encodeURIComponent(digits)}`)
                        .then(res => setCustSug({ field: 'phone', items: res.data.data || [] }))
                        .catch(() => {});
                } else {
                    setCustSug({ field: null, items: [] });
                }
            } else {
                setCustSug({ field: null, items: [] });
            }
        }, 300);
    };

    const handleSelectCustSuggestion = (c) => {
        setNewCustomerName(c.customerName || '');
        setNewPhoneNumber(c.phoneNumber || '');
        // 지목한 고객 id 를 보존해 서버로 보낸다. 이게 없으면 전화번호 없는(이름만) 고객은
        // 서버가 이름으로 다시 찾지 못해 동명이인이 새로 생긴다.
        setLinkedCustomer(c.id ? { id: c.id, hasPhone: !!c.phoneNumber } : null);
        setCustSug({ field: null, items: [] });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (isSubmitting) return; // 더블클릭/연타 방어 (이벤트 레벨)

        if (!selectedMenu) {
            setError('메뉴를 선택해주세요.');
            return;
        }

        // 🚀 유효성 검사: 연락처 입력 시 최소 4자리 확인
        if (newPhoneNumber && newPhoneNumber.replace(/\D/g, '').length < 4) {
            setError('연락처는 최소 4자리 이상 입력해주세요.');
            return;
        }

        // 가격표에 없는 조합이면 0원으로 계산된다는 걸 알린다 (차단 아님)
        if (!confirmUnpriced({ menus, options, prices, sizeId, menuId: selectedMenu.id, optionIds })) return;

        setIsSubmitting(true);
        try {
            const response = await api.post('/jobs', {
                vehicleId: vehicle.id,
                courseId: selectedMenu.id,
                optionIds: optionIds,
                finalPrice: totalPrice,
                comment: comment,
                paymentMethod: paymentMethod,
                adjustmentAmount: signedAdjustment,
                adjustmentReason: adjustment.reason,
                bayNumber: null,
                customerName: newCustomerName,
                phoneNumber: newPhoneNumber,
                customer_id: linkedCustomer?.id ?? null,
                status: 'completed'
            });

            if(onSave) onSave(response.data);
        } catch (err) {
            console.error('Submit error:', err);
            setError(err.response?.data?.error || '서비스 기록 저장에 실패했습니다.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Card className="mt-4 border-0 shadow-sm">
            <Card.Header className="bg-primary text-white fw-bold d-flex justify-content-between align-items-center">
                <span><i className="bi bi-plus-circle me-2"></i>서비스 기록 추가</span>
                {!vehicle.customer_id && <Badge bg="warning" text="dark" className="small fw-normal">고객 미등록 차량</Badge>}
            </Card.Header>
            <Card.Body className="p-4">
                <Form onSubmit={handleSubmit}>
                    {error && <Alert variant="danger">{error}</Alert>}
                    
                    {/* 🚀 고객 정보 등록 섹션 (미등록 차량일 때만 노출) */}
                    {!vehicle.customer_id && (
                        <div className="bg-light p-3 rounded mb-4 border-start border-warning border-4">
                            <h6 className="fw-bold mb-3 text-muted small uppercase"><i className="bi bi-person-plus me-2"></i>단골 등록 (선택사항)</h6>
                            <Row className="g-2">
                                <Col md={5} className="cl-wrap">
                                    <Form.Control
                                        placeholder="고객 성함"
                                        value={newCustomerName}
                                        onChange={(e) => handleCustSearch('name', e.target.value)}
                                        autoComplete="off"
                                        readOnly={!!linkedCustomer}
                                        className={`shadow-sm small ${linkedCustomer ? 'cl-locked' : 'border-0'}`}
                                    />
                                    {linkedCustomer && (
                                        <UnlinkButton onUnlink={() => setLinkedCustomer(null)} />
                                    )}
                                    {custSug.field === 'name' && custSug.items.length > 0 && (
                                        <ListGroup className="position-absolute w-100 shadow" style={{ zIndex: 5, maxHeight: '180px', overflowY: 'auto' }}>
                                            {custSug.items.map(c => (
                                                <ListGroup.Item key={c.id} action onClick={() => handleSelectCustSuggestion(c)} className="small py-2">
                                                    {c.customerName}{c.phoneNumber ? ` · ${c.phoneNumber}` : ''}
                                                </ListGroup.Item>
                                            ))}
                                        </ListGroup>
                                    )}
                                </Col>
                                <Col md={7} className="cl-wrap">
                                    <Form.Control
                                        placeholder="연락처 (번호만 입력)"
                                        value={newPhoneNumber}
                                        onChange={(e) => handleCustSearch('phone', e.target.value)}
                                        autoComplete="off"
                                        readOnly={!!linkedCustomer?.hasPhone}
                                        className={`shadow-sm small ${linkedCustomer?.hasPhone ? 'cl-locked' : 'border-0'}`}
                                    />
                                    {custSug.field === 'phone' && custSug.items.length > 0 && (
                                        <ListGroup className="position-absolute w-100 shadow" style={{ zIndex: 5, maxHeight: '180px', overflowY: 'auto' }}>
                                            {custSug.items.map(c => (
                                                <ListGroup.Item key={c.id} action onClick={() => handleSelectCustSuggestion(c)} className="small py-2">
                                                    {c.customerName}{c.phoneNumber ? ` · ${c.phoneNumber}` : ''}
                                                </ListGroup.Item>
                                            ))}
                                        </ListGroup>
                                    )}
                                </Col>
                            </Row>
                            {linkedCustomer && !linkedCustomer.hasPhone ? (
                                <div className="mt-2 cl-hint">
                                    <i className="bi bi-info-circle me-1"></i> 연결된 고객입니다. 연락처를 입력하면 이 고객에게 저장되어 다음 방문부터 번호로 자동 매칭됩니다. 다른 사람이면 ✕ 로 연결을 해제하세요.
                                </div>
                            ) : (
                                <div className="mt-2 text-muted" style={{ fontSize: '0.75rem' }}>
                                    <i className="bi bi-info-circle me-1"></i> 이름 또는 연락처만 입력해도 고객 리스트에 자동 등록됩니다. 다음 방문부터는 차량 조회 시 자동으로 표시됩니다.
                                </div>
                            )}
                        </div>
                    )}
                    
                    <JobServiceFields
                        menus={menus}
                        options={options}
                        prices={prices}
                        sizeId={sizeId}
                        value={{ service_menu_id: selectedMenu?.id ?? '', optionIds }}
                        onChange={handleServiceChange}
                    />

                    <Form.Control 
                        as="textarea" 
                        rows={2} 
                        value={comment} 
                        onChange={(e) => setComment(e.target.value)} 
                        placeholder="작업 코멘트..." 
                        className="mb-4 bg-light border-0" 
                    />
                    
                    <div className="bg-light p-3 rounded mb-4">
                        <div className="d-flex justify-content-between mb-2">
                            <span className="text-muted small">선택 합계</span>
                            <span className="small">{calculatedPrice.toLocaleString()}원</span>
                        </div>
                        <div className="mb-2">
                            <AdjustmentInput adjustment={adjustment} />
                            {signedAdjustment !== 0 && (
                                <div className="d-flex justify-content-between mt-2 small">
                                    <span className="text-muted">{adjustment.mode === 'discount' ? '할인' : '추가'}</span>
                                    <span className={adjustment.mode === 'discount' ? 'text-danger' : 'text-primary'}>
                                        {signedAdjustment > 0 ? '+' : ''}{signedAdjustment.toLocaleString()}원
                                    </span>
                                </div>
                            )}
                        </div>
                        <div className="d-flex justify-content-between align-items-center border-top pt-2">
                            <h5 className="mb-0 fw-bold">최종 결제 금액</h5>
                            <h4 className="mb-0 fw-bold text-primary">{totalPrice.toLocaleString()}원</h4>
                        </div>
                    </div>

                    <div className="d-flex gap-2">
                        <Form.Select
                            value={paymentMethod} 
                            onChange={(e) => setPaymentMethod(e.target.value)} 
                            className="w-auto border-0 bg-light"
                        >
                            <option value="카드">카드</option>
                            <option value="현금">현금</option>
                            <option value="이체">이체</option>
                            <option value="포인트" disabled={!vehicle.customer_id && !newPhoneNumber}>포인트</option>
                        </Form.Select>
                        <Button variant="primary" type="submit" className="flex-grow-1 fw-bold" disabled={isSubmitting}>
                            {isSubmitting ? '저장 중...' : '저장하기'}
                        </Button>
                    </div>
                </Form>
            </Card.Body>
        </Card>
    );
};

export default AddServiceRecord;
